// Fatos legíveis de uma solicitação de abastecimento (D-10, H-05): sem UUID, ausência sempre rotulada.
import type { EstadoSolicitacaoAbastecimento, OrigemSolicitacaoAbastecimento, SepararSolicitacaoAbastecimentoInput, SituacaoFilaAbastecimento, SolicitarAbastecimentoView } from '@pieltec/contracts';
import { formatarDataHoraOperacional, formatarQuantidadeDecimal } from '../../lib/data-hora-operacional';

export interface LinhaFila {
  /** Id da SOLICITAÇÃO: repete-se entre os itens de uma solicitação derivada da liberação. */
  readonly id: string;
  /** Id do item da solicitação (material). */
  readonly itemId: string;
  /** Identidade única da linha na fila (solicitação + item): chave de render e de seleção. */
  readonly chave: string;
  /** Estado DESTE item (separação, ajuste e entrega são por item); não o agregado da solicitação. */
  readonly estado: EstadoSolicitacaoAbastecimento;
  readonly situacao: SituacaoFilaAbastecimento | null;
  readonly modo: 'LOT_OP' | 'SETOR';
  readonly origem: OrigemSolicitacaoAbastecimento;
  /** Rótulo curto da origem (D-11): demanda da liberação ou pedido adicional de qualquer canal. */
  readonly origemTexto: string;
  readonly destino: string;
  readonly natureza: string;
  readonly material: string;
  readonly unidade: string;
  readonly solicitado: string;
  readonly separado: string;
  readonly entregue: string;
  readonly pendente: string;
  readonly solicitadoTexto: string;
  readonly separadoTexto: string;
  readonly entregueTexto: string;
  readonly pendenteTexto: string;
  readonly solicitante: string;
  readonly criadaEm: string;
}

export const NATUREZA: Record<'LOT_OP' | 'SETOR', string> = {
  LOT_OP: 'Transferência para o destino',
  SETOR: 'Baixa na entrega conforme regra de consumíveis',
};

export const ORIGEM_ROTULO: Record<OrigemSolicitacaoAbastecimento, string> = {
  LIBERACAO: 'Liberação',
  ADICIONAL: 'Pedido adicional',
};

/** Uma solicitação LOT_OP derivada da liberação tem um item por material: só solicitação + item identifica a linha. */
export function chaveDaLinha(solicitacaoId: string, itemId: string): string {
  return `${solicitacaoId}:${itemId}`;
}

/**
 * Linha atual da fila para uma ação aberta a partir de um cartão. Resolve SÓ pela chave do cartão clicado e confere a
 * identidade: se a linha saiu da fila (filtro, recarga, outra pessoa), devolve null e a tela mostra "recarregar",
 * nunca outra solicitação ou outro item.
 */
export function linhaSelecionada(itens: readonly LinhaFila[], chave: string | null): LinhaFila | null {
  if (chave === null) return null;
  const linha = itens.find((item) => item.chave === chave) ?? null;
  return linha !== null && linha.chave === chave ? linha : null;
}

/**
 * Corpo da separação a partir do cartão: a separação mira o ITEM do cartão (`solicitacaoItemId`), porque uma
 * solicitação com vários materiais é recusada pelo servidor sem ele e nunca se separa um item ao acaso.
 */
export function entradaSeparacao(linha: Pick<LinhaFila, 'itemId'>, localizacaoId: string, quantidade: string, chaveIdempotencia: string): SepararSolicitacaoAbastecimentoInput {
  return { solicitacaoItemId: linha.itemId, localizacaoId: localizacaoId.trim(), quantidade: quantidade.trim().replace(',', '.'), chaveIdempotencia };
}

/** Fatos legíveis do cartão (D-10, H-05): nada de UUID, ausência sempre rotulada. */
export function linhaDaFila(item: SolicitarAbastecimentoView): LinhaFila {
  const destino = item.modo === 'LOT_OP'
    ? `Lote ${item.loteCodigo ?? 'não informado'} · OP ${item.opNumero ?? 'não informada'}`
    : `Setor ${item.setorCodigo ?? 'não informado'}${item.setorNome ? ` · ${item.setorNome}` : ''}`;
  const material = item.materialCodigo || item.materialNome
    ? `${item.materialCodigo || 'Código não informado'} · ${item.materialNome || 'Nome do material não informado'}`
    : 'Material não informado';
  return {
    id: item.id, itemId: item.itemId, chave: chaveDaLinha(item.id, item.itemId), estado: item.estadoItem, situacao: item.situacaoFila, modo: item.modo, origem: item.origem, origemTexto: ORIGEM_ROTULO[item.origem], destino, natureza: NATUREZA[item.modo], material, unidade: item.unidade,
    solicitado: item.quantidadeVigente, separado: item.quantidadeSeparada, entregue: item.quantidadeEntregue, pendente: item.saldoPendente,
    solicitadoTexto: formatarQuantidadeDecimal(item.quantidadeVigente, item.unidade), separadoTexto: formatarQuantidadeDecimal(item.quantidadeSeparada, item.unidade),
    entregueTexto: formatarQuantidadeDecimal(item.quantidadeEntregue, item.unidade), pendenteTexto: formatarQuantidadeDecimal(item.saldoPendente, item.unidade),
    solicitante: item.solicitanteNome || 'Solicitante não informado', criadaEm: formatarDataHoraOperacional(item.createdAt),
  };
}

